import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert, Anchor, Badge, Center, Code, Group, Loader, Pagination, Paper, Skeleton as MSkeleton, Stack, Table, Text,
  Tooltip,
} from '@mantine/core'
import { IconAlertCircle, IconCircleCheck, IconDatabaseOff, IconInfoCircle } from '@tabler/icons-react'
import { EmptyState, PageTitle } from '@lighthouse/design'
import { RestrictedState } from '../components/access/RestrictedState'
import { BackstopDrawer } from '../components/requests/BackstopDrawer'
import {
  BACKSTOP_EMPTY_MESSAGE, BACKSTOP_EMPTY_TITLE, BACKSTOP_SCOPE, kindLabel, reasonHint, reasonLabel, routedToText,
  subjectLine, summaryLine,
} from '../components/requests/requestBackstopFormat'
import { useLighthouseAdmin } from '../hooks/useLighthouseAdmin'
import { useBackstopRequests, useRequestBackstopSummary } from '../hooks/useRequestBackstop'
import { isApiError, problemDetailText } from '../services/apiError'
import { BACKSTOP_PAGE_SIZE } from '../types/requestBackstop'
import type { BackstopRequest, BackstopRequestList, RequestBackstopSummary } from '../types/requestBackstop'
import { adminPageMayFetch } from '../utils/adminGate'
import { NOT_MEASURED, agoLabel, errorKind, errorStatus, errorSummary, exactNumber, whenLabel } from '../utils/format'

const SURFACE = 'The request backstop'

/** What the 503 and an unprovisioned summary both say: the request table is missing here. */
const UNPROVISIONED =
  "Cartographer's request table does not exist in this database (migration AddCartographerRequest), so there is "
  + 'nothing to list — which is not the same as nothing having fallen through.'

/**
 * Request Backstop (Breakwater) — the Cartographer requests that fell through, over `api/observatory/requests`
 * (Cartographer doc 15 §7, W5-6). Rung 3 of doc 10's ladder: Observatory is the last line of defence, not the queue.
 *
 *  1. **Only what fell through.** The server lists open requests at the Admin tier: a decision overdue, a request
 *     nobody whose move it is could be told about, and platform access, which starts here. Each row says which. An
 *     empty page is success, and says so (doc 10 §1).
 *  2. **The one act surface over Cartographer data** (D6a as amended by Q-2b = a). Every other Cartographer-section page
 *     is read-only. The drawer's interventions — force close, reassign, reroute, a corrected due date, and platform
 *     access's forward, attest and refuse — are the server's matrix for a Lighthouse administrator, and render only on
 *     the catalogue's `canHandleRequests`. Nothing here approves, or refuses access to content.
 *  3. **The gate is the server's.** The page and its reads follow `useLighthouseAdmin` (`permission.isLighthouseAdmin`),
 *     never token roles: denied renders RestrictedState and fetches nothing; pending fetches nothing.
 *  4. **One request is a route**, `/requests/:requestId`, because that is where a Cartographer notice's
 *     `observatory:/requests/{id}` link lands.
 */
export function RequestBackstopPage() {
  const gate = useLighthouseAdmin()
  const mayFetch = adminPageMayFetch(gate)
  const navigate = useNavigate()
  const { requestId } = useParams()
  const [page, setPage] = useState(1)

  const summary = useRequestBackstopSummary({ enabled: mayFetch })
  const provisioned = summary.data?.storeProvisioned === true
  const list = useBackstopRequests(
    { skip: (page - 1) * BACKSTOP_PAGE_SIZE, top: BACKSTOP_PAGE_SIZE },
    { enabled: mayFetch && provisioned },
  )

  return (
    <Stack gap="lg" pb="xl">
      <PageTitle subtitle="Breakwater — the Cartographer requests that fell through, and the interventions a Lighthouse administrator may make on them">
        Request Backstop
      </PageTitle>

      {gate.state === 'denied' ? (
        <RestrictedState surface={SURFACE} reason={gate.reason} />
      ) : gate.state === 'pending' ? (
        <Stack gap="md" data-backstop-page="pending" aria-busy="true">
          <MSkeleton height={48} />
          <MSkeleton height={240} />
        </Stack>
      ) : (
        <>
          <Alert variant="light" color="gray" icon={<IconInfoCircle size={16} />} data-backstop-scope="">
            <Text size="sm">{BACKSTOP_SCOPE}</Text>
          </Alert>

          <SummarySection query={summary} />

          {provisioned && (
            <ListSection
              query={list}
              page={page}
              onPage={setPage}
              onOpen={(id) => navigate(`/requests/${encodeURIComponent(id)}`)}
            />
          )}
        </>
      )}

      <BackstopDrawer requestId={requestId ?? null} enabled={mayFetch} onClose={() => navigate('/requests')} />
    </Stack>
  )
}

interface QuerySlice<T> {
  data: T | undefined
  isLoading: boolean
  isError: boolean
  error: unknown
}

function SummarySection({ query }: { query: QuerySlice<RequestBackstopSummary> }) {
  if (query.isError) return <BackstopError error={query.error} />
  if (query.isLoading || !query.data) {
    return (
      <Stack gap="md" data-backstop-page="loading" aria-busy="true">
        <MSkeleton height={48} />
        <MSkeleton height={240} />
      </Stack>
    )
  }

  const summary = query.data
  if (!summary.storeProvisioned) {
    return (
      <Alert icon={<IconDatabaseOff size={16} />} color="yellow" variant="light" title="Request store not provisioned" data-backstop-unprovisioned="">
        <Text size="sm">{UNPROVISIONED}</Text>
      </Alert>
    )
  }

  return (
    <Stack gap={2} data-backstop-summary="">
      <Text fw={600}>{summaryLine(summary)}</Text>
      {summary.oldestOnBackstopSince && (
        <Text size="xs" c="dimmed">The longest-waiting arrived {agoLabel(summary.oldestOnBackstopSince)}.</Text>
      )}
    </Stack>
  )
}

function ListSection({ query, page, onPage, onOpen }: {
  query: QuerySlice<BackstopRequestList>
  page: number
  onPage: (page: number) => void
  onOpen: (id: string) => void
}) {
  if (query.isError) return <BackstopError error={query.error} />

  // `|| !data` is load-bearing: between retries the flags read "not loading, not errored" with no payload, which would
  // otherwise render as an empty backstop — the one reading this page must never fake.
  if (query.isLoading || !query.data) {
    return <Center py="xl" data-backstop-list="loading"><Loader /></Center>
  }

  const list = query.data
  if (list.items.length === 0 && list.totalCount === 0) {
    return (
      <Paper p="md" withBorder style={{ backgroundColor: 'var(--color-bg-surface)' }} data-backstop-empty="">
        <EmptyState
          icon={<IconCircleCheck size={28} stroke={1.5} />}
          title={BACKSTOP_EMPTY_TITLE}
          message={BACKSTOP_EMPTY_MESSAGE}
        />
      </Paper>
    )
  }

  const totalPages = Math.max(1, Math.ceil(list.totalCount / BACKSTOP_PAGE_SIZE))

  return (
    <Paper p="md" withBorder style={{ backgroundColor: 'var(--color-bg-surface)' }} data-backstop-list="">
      <Table.ScrollContainer minWidth={1000}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Request</Table.Th>
              <Table.Th>Asked by</Table.Th>
              <Table.Th>Routed to</Table.Th>
              <Table.Th>Why it is here</Table.Th>
              <Table.Th>On the backstop since</Table.Th>
              <Table.Th>Due</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {list.items.map((request) => (
              <BackstopRow key={request.id} request={request} onOpen={onOpen} />
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <Group justify="space-between" mt="md">
        <Text size="sm" c="dimmed">
          {exactNumber(list.totalCount)} {list.totalCount === 1 ? 'request' : 'requests'} on the backstop
        </Text>
        {totalPages > 1 && <Pagination value={page} total={totalPages} onChange={onPage} />}
      </Group>
    </Paper>
  )
}

function BackstopRow({ request, onOpen }: { request: BackstopRequest; onOpen: (id: string) => void }) {
  const hint = reasonHint(request.backstopReason)
  return (
    <Table.Tr data-backstop-row={request.id}>
      <Table.Td>
        <Stack gap={2} align="flex-start">
          <Badge size="xs" variant="light" color="gray" style={{ textTransform: 'none' }}>{kindLabel(request.kind)}</Badge>
          <Anchor component="button" type="button" size="sm" fw={500} ta="left" onClick={() => onOpen(request.id)}>
            {subjectLine(request)}
          </Anchor>
        </Stack>
      </Table.Td>
      <Table.Td><Text size="sm">{request.requesterDisplayName?.trim() || NOT_MEASURED}</Text></Table.Td>
      <Table.Td><Text size="sm">{routedToText(request)}</Text></Table.Td>
      <Table.Td>
        <Tooltip label={hint ?? ''} multiline w={300} disabled={!hint}>
          <Badge size="sm" color="orange" variant="light" style={{ textTransform: 'none' }} data-backstop-reason={request.backstopReason ?? ''}>
            {reasonLabel(request.backstopReason)}
          </Badge>
        </Tooltip>
      </Table.Td>
      <Table.Td>
        <Tooltip label={whenLabel(request.onBackstopSince)} disabled={!request.onBackstopSince}>
          <Text size="sm">{agoLabel(request.onBackstopSince)}</Text>
        </Tooltip>
      </Table.Td>
      <Table.Td><Text size="sm">{request.dueAt ? whenLabel(request.dueAt) : NOT_MEASURED}</Text></Table.Td>
    </Table.Tr>
  )
}

/**
 * A read failed. A 403 is the missing role (RestrictedState); a 503 is the missing table; anything else is a fault,
 * and says that nothing is known — which is not the same as nothing having fallen through.
 */
function BackstopError({ error }: { error: unknown }) {
  const kind = errorKind(error)
  if (kind === 'forbidden') {
    return <RestrictedState surface={SURFACE} reason={isApiError(error) ? problemDetailText(error.body) : null} />
  }
  if (errorStatus(error) === 503) {
    return (
      <Alert icon={<IconDatabaseOff size={16} />} color="yellow" variant="light" title="Request store not provisioned" data-backstop-unprovisioned="">
        <Text size="sm">{UNPROVISIONED}</Text>
      </Alert>
    )
  }

  const summary = errorSummary(error)
  return (
    <div data-backstop-error={kind}>
      <EmptyState
        icon={<IconAlertCircle size={28} stroke={1.5} />}
        title="Could not read the backstop"
        message="The request failed, so nothing is known here about any request. That is not the same as nothing having fallen through."
        action={summary ? <Code style={{ overflowWrap: 'anywhere' }}>{summary}</Code> : undefined}
      />
    </div>
  )
}
